import { authApi, clearAccessToken, configureAuthRecovery, setAccessToken } from "@vv/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { ForgotPasswordRequest, LoginRequest, ResetPasswordRequest } from "@vv/types";
import type { AppStore, RootState } from "../store";
import {
  authFailed,
  authRequestStarted,
  authSucceeded,
  loggedOut,
  persistAuth,
  tokensRefreshed,
  userLoaded,
} from "./auth.slice";

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

export const login = createAsyncThunk<void, LoginRequest, { state: RootState }>(
  "auth/login",
  async (payload, { dispatch }) => {
    dispatch(authRequestStarted());
    try {
      const { user, accessToken, refreshToken } = await authApi.login(payload);
      setAccessToken(accessToken);
      persistAuth({ user, accessToken, refreshToken });
      dispatch(authSucceeded({ user, accessToken, refreshToken }));
    } catch (err) {
      dispatch(authFailed(errorMessage(err)));
      throw err;
    }
  },
);

/** Used by SsoCallbackPage after the backend redirects back with tokens in the query string. */
export const loginWithSsoTokens = createAsyncThunk<
  void,
  { accessToken: string; refreshToken: string },
  { state: RootState }
>("auth/loginWithSsoTokens", async ({ accessToken, refreshToken }, { dispatch }) => {
  dispatch(authRequestStarted());
  setAccessToken(accessToken);
  try {
    const user = await authApi.me();
    persistAuth({ user, accessToken, refreshToken });
    dispatch(authSucceeded({ user, accessToken, refreshToken }));
  } catch (err) {
    clearAccessToken();
    dispatch(authFailed(errorMessage(err)));
    throw err;
  }
});

/** Re-hydrates `user` from the backend using a persisted access token (e.g. on app boot). */
export const loadMe = createAsyncThunk<void, void, { state: RootState }>(
  "auth/loadMe",
  async (_, { dispatch, getState }) => {
    const { accessToken } = getState().auth;
    if (!accessToken) return;
    setAccessToken(accessToken);
    try {
      const user = await authApi.me();
      dispatch(userLoaded(user));
    } catch (err) {
      dispatch(authFailed(errorMessage(err)));
    }
  },
);

export const logout = createAsyncThunk<void, void, { state: RootState }>(
  "auth/logout",
  async (_, { dispatch }) => {
    try {
      await authApi.logout();
    } catch {
      // best-effort; proceed with client-side logout regardless of server outcome
    } finally {
      clearAccessToken();
      persistAuth(null);
      dispatch(loggedOut());
    }
  },
);

/**
 * These don't mutate `auth` slice state (the user isn't logged in yet when
 * requesting/completing a reset), so there's nothing to dispatch — the page
 * awaits the thunk and catches rejections to show a toast.
 */
export const forgotPassword = createAsyncThunk<void, ForgotPasswordRequest, { state: RootState }>(
  "auth/forgotPassword",
  async (payload) => {
    await authApi.forgotPassword(payload);
  },
);

export const resetPassword = createAsyncThunk<void, ResetPasswordRequest, { state: RootState }>(
  "auth/resetPassword",
  async (payload) => {
    await authApi.resetPassword(payload);
  },
);

/**
 * Pages must never import @vv/api directly (see the layering rule in
 * README.md) — this thin re-export is the one sanctioned way to build the
 * Microsoft SSO redirect URL from `state`.
 */
export function getMicrosoftSsoLoginUrl(apiBaseUrl: string): string {
  return authApi.microsoftSsoLoginUrl(apiBaseUrl);
}

/**
 * Wires the api-client's single-flight 401-recovery hook to this store. Call
 * once at app startup (see apps/web/src/main.tsx). This is the one place
 * that bridges `api` (which has no knowledge of Redux) back into `state`,
 * keeping the dependency arrow types -> api -> state -> ui one-directional.
 */
export function registerAuthRecovery(store: AppStore): void {
  configureAuthRecovery(async () => {
    const { refreshToken, user } = store.getState().auth;
    if (!refreshToken) return null;
    try {
      const result = await authApi.refresh({ refreshToken });
      setAccessToken(result.accessToken);
      if (user) {
        persistAuth({
          user,
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
        });
      }
      store.dispatch(tokensRefreshed(result));
      return result.accessToken;
    } catch {
      clearAccessToken();
      persistAuth(null);
      store.dispatch(loggedOut());
      return null;
    }
  });
}
